import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import {
  ASafetyCheck,
  ASafetyCheckWithAnswerFormik,
} from "../../types/safetycheck";
import { useSearchParams } from "react-router-dom";
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { COLORS } from "../../assets/theme";
import useButtonStyles from "../../hooks/styles/useButtonStyles";

type Props = {
  questionsList?: {
    title: string;
    list: ASafetyCheck[];
  }[];
  initialValues: ASafetyCheckWithAnswerFormik;
  onSubmit: (
    values: ASafetyCheckWithAnswerFormik,
    _?: FormikHelpers<ASafetyCheckWithAnswerFormik>
  ) => void;
  loading?: boolean;
  isSubmittingCheck?: boolean;
  showSubmitCheck: boolean;
  renderButton?: React.ReactNode;
  isDisabledRadio?: boolean;
};

const useStyles = makeStyles((_) => ({
  text: {
    fontFamily: "'Raleway', sans-serif",
  },
  headerText: {
    fontWeight: "bold",
    color: COLORS.text_black,
    fontSize: 12,
    textTransform: "uppercase",
  },
  labelText: {
    fontWeight: "bold",
    color: COLORS.text_black,
    fontSize: 12,

    padding: "9px 8px",
  },
  radio: {
    // color:
    color: COLORS.azure,
    "&$checked": {
      color: COLORS.azure,
    },
  },

  radioLabel: {
    "&>span": {
      fontFamily: "'Raleway', sans-serif",
      fontWeight: "bold",
    },
  },

  withPadding: {
    padding: "9px 8px",
  },

  detail: {
    paddingTop: 0,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 0,
  },

  bottom_nav: {
    width: "100%",
    maxWidth: "500px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "30px 24px",
    // backgroundColor: "red",
  },
}));

const ChecklistQuestionAnswerComponent = ({
  initialValues,
  onSubmit,
  loading,
  questionsList,
  isSubmittingCheck,
  showSubmitCheck = true,
  renderButton,
  isDisabledRadio = false,
}: Props) => {
  const classes = useStyles();
  const [params, setParams] = useSearchParams();
  const page = params.get("p") ?? "1";
  const activePage = parseInt(page);
  const divRef = React.useRef<HTMLDivElement[]>([]);
  const btnClasses = useButtonStyles();
  React.useEffect(() => {
    if (!params.get("p")) {
      params.set("p", "1");
      setParams(params, { replace: true });
    }
  }, [params]);
  React.useEffect(() => {
    divRef.current = divRef.current.slice(0, questionsList?.length);
  }, [questionsList?.length]);
  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {({ handleChange, isSubmitting, values }) => {
        const activeCategory =
          (questionsList &&
            questionsList.find((_, index) => index === activePage - 1)
              ?.title) ??
          "";
        const existValues = values.data.filter(
          (value) => value.category === activeCategory
        );
        const isSomeValuesByCategoryAreNull = existValues.some(
          (value) => value.answer === null
        );
        return (
          <Form id="safetyChecklistForm">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 8,
                }}
              >
                <CircularProgress
                  size={24}
                  color="inherit"
                  style={{ color: COLORS.blue_1 }}
                />
              </div>
            ) : questionsList && questionsList.length > 0 ? (
              <>
                {questionsList.map(({ title, list }, idx) => {
                  return (
                    <div
                      ref={(ref) => divRef.current[idx] === ref}
                      key={title}
                      style={{
                        display: activePage === idx + 1 ? "contents" : "none",
                      }}
                    >
                      <Typography
                        className={[
                          classes.text,
                          classes.headerText,
                          classes.withPadding,
                        ].join(" ")}
                      >
                        {idx + 1}. {title}
                      </Typography>
                      <div
                        className={classes.withPadding}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {list.map(({ category, seq, question }, index) => {
                          return (
                            <React.Fragment key={`${category}-${seq}-${index}`}>
                              <FormControl>
                                <FormLabel
                                  className={[
                                    classes.text,
                                    classes.labelText,
                                  ].join(" ")}
                                >
                                  {question}?
                                </FormLabel>
                                <RadioGroup
                                  row
                                  name={`data[${seq - 1}].answer`}
                                  onChange={handleChange}
                                  style={{
                                    justifyContent: "space-around",
                                  }}
                                  value={
                                    values.data.find((item) => item.seq === seq)
                                      ?.answer ?? null
                                  }
                                >
                                  <FormControlLabel
                                    value="y"
                                    className={[
                                      classes.text,
                                      classes.radioLabel,
                                    ].join(" ")}
                                    control={
                                      <Radio
                                        color="default"
                                        disabled={isDisabledRadio}
                                        className={classes.radio}
                                      />
                                    }
                                    label="YA"
                                  />
                                  <FormControlLabel
                                    value="n"
                                    className={[
                                      classes.text,
                                      classes.radioLabel,
                                    ].join(" ")}
                                    control={
                                      <Radio
                                        color="default"
                                        disabled={isDisabledRadio}
                                        className={classes.radio}
                                      />
                                    }
                                    label="TIDAK"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div className={classes.withPadding}>
                  {activePage === questionsList?.length ? (
                    showSubmitCheck ? (
                      <button
                        className={[
                          btnClasses.button,
                          isSubmitting ||
                          isSubmittingCheck ||
                          isSomeValuesByCategoryAreNull
                            ? "disabled"
                            : "secondary",
                          "full-width",
                        ].join(" ")}
                        type="submit"
                        form="safetyChecklistForm"
                        disabled={
                          isSubmitting ||
                          isSubmittingCheck ||
                          isSomeValuesByCategoryAreNull
                        }
                        style={{ height: 48, fontSize: 12 }}
                      >
                        {isSubmitting || isSubmittingCheck
                          ? "Loading..."
                          : "SIMPAN SAFETY CHECKLIST"}
                      </button>
                    ) : (
                      renderButton
                    )
                  ) : null}
                </div>
                {questionsList && questionsList.length > 0 ? (
                  <div className={[classes.bottom_nav].join(" ")}>
                    <button
                      style={{
                        visibility: activePage <= 1 ? "hidden" : "visible",
                      }}
                      type="button"
                      className={[btnClasses.button, "white"].join(" ")}
                      disabled={activePage <= 1}
                      onClick={() => {
                        params.set("p", `${activePage - 1}`);
                        setParams(params);
                      }}
                    >
                      Sebelumnya
                    </button>
                    <button
                      style={{
                        visibility:
                          activePage === questionsList?.length
                            ? "hidden"
                            : "visible",
                      }}
                      type="button"
                      className={[
                        btnClasses.button,
                        isSomeValuesByCategoryAreNull
                          ? "disabled"
                          : "secondary",
                      ].join(" ")}
                      disabled={
                        activePage === questionsList?.length ||
                        isSomeValuesByCategoryAreNull
                      }
                      onClick={() => {
                        window.scrollTo({ top: 0 });
                        params.set("p", `${activePage + 1}`);
                        setParams(params);
                      }}
                    >
                      Berikutnya
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <p style={{ textAlign: "center" }}>
                Daftar pertanyaan checklist tidak tersedia.
              </p>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ChecklistQuestionAnswerComponent;
